'use client';

import { useEffect, useState } from 'react';
import { pageWindow } from '@/lib/paginate';

/**
 * The pager for a text too long to translate in one request.
 *
 * ── Why it floats ────────────────────────────────────────────────────────────
 *
 * Fixed to the bottom of the window, centred, above everything. A pager in the
 * flow of the page would sit under two panes that are often taller than the
 * screen, which puts the only way to reach page 4 somewhere you have to go
 * looking for — and looking for it means scrolling past the very translation you
 * were reading. Floating, it is in the same place whatever the panes are doing,
 * which is what Google Translate does with its own arrows and for the same
 * reason.
 *
 * It exists ONLY when there is more than one page. A pager over a single page of
 * text is a control that cannot do anything.
 *
 * ── Why it says more than "‹ ›" ──────────────────────────────────────────────
 *
 * Google's bar says "Use the arrows to see the full translation", which answers
 * one question — how do I move — and leaves the three that follow it. Somebody
 * who has just pasted a long document wants to know how much of it there is,
 * where they are in it, how much of it has actually been translated, and how big
 * this piece is. All four are known here, so all four are shown: the numbered
 * buttons carry a dot when that page has an answer already, the readout on the
 * right gives the page and its size, and the line under the numbers counts what
 * is done. Nothing here is a guess.
 *
 * ── Collapsing ───────────────────────────────────────────────────────────────
 *
 * "Done" on Google's bar removes it, and with it every way of turning the page
 * except the keyboard. This one collapses to a pill that still has both arrows
 * and still says where you are — the bar gets out of the way of the text without
 * taking navigation with it, and one click brings it back.
 */
export default function PageBar({
  count,
  current,
  onGo,
  pageChars,
  docChars,
  maxChars,
  done,
  busy,
}: {
  count: number;
  /** Zero-based, like the array it indexes; every label adds the 1. */
  current: number;
  onGo: (page: number) => void;
  pageChars: number;
  docChars: number;
  maxChars: number;
  /** Pages whose translation is already in hand — instant to return to. */
  done: Set<number>;
  /** A translation is running for the current page. */
  busy: boolean;
}) {
  const [open, setOpen] = useState(true);

  // A newly split document is a new thing to navigate: if the bar was collapsed
  // for the last one, it should not stay hidden for this one.
  useEffect(() => { setOpen(true); }, [count]);

  const atStart = current <= 0;
  const atEnd = current >= count - 1;
  const doneCount = done.size;

  const arrow = (dir: -1 | 1) => {
    const disabled = dir < 0 ? atStart : atEnd;
    return (
      <button
        type="button"
        onClick={() => onGo(current + dir)}
        disabled={disabled}
        aria-label={dir < 0 ? 'Previous page' : 'Next page'}
        title={dir < 0 ? 'Previous page' : 'Next page'}
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-ink-500
                   transition hover:bg-ink-100 hover:text-ink-900 disabled:cursor-not-allowed
                   disabled:opacity-30 disabled:hover:bg-transparent
                   dark:text-ink-300 dark:hover:bg-ink-700 dark:hover:text-white"
      >
        <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor"
             strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d={dir < 0 ? 'M15 5l-7 7 7 7' : 'M9 5l7 7-7 7'} />
        </svg>
      </button>
    );
  };

  return (
    <div
      // `pointer-events-none` on the positioner, `auto` on the bar: the strip
      // spans the window, and without this it would swallow clicks on the page
      // either side of a bar that is only 600px wide.
      className="pointer-events-none fixed inset-x-0 bottom-4 z-40 flex justify-center px-3"
      role="navigation"
      aria-label="Pages of this text"
    >
      {open ? (
        <div
          className="pointer-events-auto flex max-w-full flex-col gap-1 rounded-2xl border border-ink-200
                     bg-white/95 px-2.5 py-2 shadow-2xl backdrop-blur
                     dark:border-ink-700 dark:bg-ink-800/95"
        >
          <div className="flex items-center gap-1.5">
            {/* What this bar IS, said once. Somebody meeting it for the first
                time has just pasted something long and does not yet know the
                app split it. */}
            <span className="ml-1 mr-0.5 hidden shrink-0 items-center gap-1.5 text-xs font-medium
                             text-ink-500 sm:flex dark:text-ink-300">
              <svg viewBox="0 0 24 24" className="h-4 w-4 text-accent-600 dark:text-accent-400" fill="none"
                   stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 5.5A1.5 1.5 0 015.5 4H10a2 2 0 012 2v13a1.6 1.6 0 00-1.2-1.5L4.8 16A1 1 0 014 15V5.5z" />
                <path d="M20 5.5A1.5 1.5 0 0018.5 4H14a2 2 0 00-2 2v13a1.6 1.6 0 011.2-1.5l6-1.5a1 1 0 00.8-1V5.5z" />
              </svg>
              Long text
            </span>

            {arrow(-1)}

            <div className="flex min-w-0 items-center gap-1 overflow-x-auto">
              {pageWindow(count, current).map((n, i) =>
                n === null ? (
                  <span key={`gap-${i}`} className="px-1 text-xs text-ink-400 select-none">…</span>
                ) : (
                  <button
                    key={n}
                    type="button"
                    onClick={() => onGo(n)}
                    aria-current={n === current ? 'page' : undefined}
                    title={
                      `Page ${n + 1} of ${count}` +
                      (done.has(n) ? ' — translated' : ' — not translated yet')
                    }
                    className={
                      'relative flex h-8 min-w-[2rem] shrink-0 items-center justify-center rounded-lg px-2 ' +
                      'text-sm font-medium tabular-nums transition ' +
                      (n === current
                        ? 'bg-accent-600 text-white shadow-sm'
                        : 'text-ink-600 hover:bg-ink-100 dark:text-ink-300 dark:hover:bg-ink-700')
                    }
                  >
                    {n + 1}
                    {/*
                      A dot for a page whose answer is already held — it will
                      come back instantly and costs nothing. Its absence is the
                      more useful half: it marks the pages still to be paid for.
                      Placed under the number rather than over it so it never
                      covers a digit.
                    */}
                    {done.has(n) && (
                      <span
                        aria-hidden
                        className={
                          'absolute bottom-0.5 h-1 w-1 rounded-full ' +
                          (n === current ? 'bg-white/80' : 'bg-emerald-500')
                        }
                      />
                    )}
                  </button>
                ),
              )}
            </div>

            {arrow(1)}

            <span className="mx-1 hidden h-6 w-px shrink-0 bg-ink-200 sm:block dark:bg-ink-700" />

            <span className="hidden shrink-0 whitespace-nowrap text-xs text-ink-500 sm:block dark:text-ink-400">
              <span className="font-medium text-ink-800 dark:text-ink-100">
                Page {current + 1} of {count}
              </span>
              {' · '}
              {pageChars.toLocaleString()} / {maxChars.toLocaleString()} chars
            </span>

            <button
              type="button"
              onClick={() => setOpen(false)}
              title="Collapse this bar"
              aria-label="Collapse the page bar"
              className="ml-0.5 flex h-8 shrink-0 items-center rounded-lg px-2 text-xs font-medium
                         text-ink-500 transition hover:bg-ink-100 hover:text-ink-900
                         dark:text-ink-400 dark:hover:bg-ink-700 dark:hover:text-white"
            >
              Done
            </button>
          </div>

          {/*
            The second line: the document as a whole, and how much of it has an
            answer. It is the question the first line cannot answer — "am I
            nearly through this" — and it is also the honest statement of what
            paging costs, since an untranslated page is a request nobody has
            made yet.
          */}
          <div className="flex items-center gap-2 px-1 pb-0.5 text-[11px] text-ink-400">
            <span className="tabular-nums">{docChars.toLocaleString()} characters in total</span>
            <span aria-hidden>·</span>
            <span className="flex items-center gap-1 tabular-nums">
              <span
                aria-hidden
                className={
                  'h-1.5 w-1.5 rounded-full ' +
                  (busy ? 'animate-pulse bg-accent-500' : doneCount === count ? 'bg-emerald-500' : 'bg-ink-300')
                }
              />
              {busy
                ? 'translating this page…'
                : `${doneCount} of ${count} pages translated`}
            </span>
            <span aria-hidden className="hidden sm:inline">·</span>
            <span className="hidden sm:inline">each page is translated when you open it</span>
          </div>
        </div>
      ) : (
        <div
          className="pointer-events-auto flex items-center gap-0.5 rounded-full border border-ink-200
                     bg-white/95 px-1 py-1 shadow-xl backdrop-blur
                     dark:border-ink-700 dark:bg-ink-800/95"
        >
          {arrow(-1)}
          <button
            type="button"
            onClick={() => setOpen(true)}
            title="Show the page bar"
            className="flex h-8 items-center rounded-full px-2 text-xs font-semibold tabular-nums
                       text-ink-700 transition hover:bg-ink-100 dark:text-ink-100 dark:hover:bg-ink-700"
          >
            {current + 1} / {count}
          </button>
          {arrow(1)}
        </div>
      )}
    </div>
  );
}
